/**
 * AutoApproveDialog - Phase B / F-B2
 * ====================================
 *
 * Preview modal that lets the user batch-approve all High-confidence
 * (≥ AI auto-post threshold) pending transactions in one click.
 *
 *   • Lists the candidate count and average confidence
 *   • Shows up to 5 sample rows
 *   • Confirms → calls the existing handleQuickApprove for each candidate
 */
import React, { useMemo, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Sparkles, Loader2, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { pickCategoryDisplay } from './bankFeedDisplay';

export const AutoApproveDialog = ({
  open,
  onOpenChange,
  candidates = [],          // transactions whose confidence ≥ threshold
  threshold = 0.80,
  currency = 'NGN',
  onConfirm,                // async (txs) => void
}) => {
  const [busy, setBusy] = useState(false);

  const stats = useMemo(() => {
    if (candidates.length === 0) return { count: 0, avg: 0, totalIn: 0, totalOut: 0 };
    let sum = 0, totalIn = 0, totalOut = 0;
    for (const t of candidates) {
      sum += (t.confidence ?? t.ai_confidence ?? 0);
      const amt = Math.abs(t.amount || 0);
      if (t.direction === 'inflow' || (t.amount || 0) > 0) totalIn += amt; else totalOut += amt;
    }
    return { count: candidates.length, avg: sum / candidates.length, totalIn, totalOut };
  }, [candidates]);

  const samples = candidates.slice(0, 5);

  const handleConfirm = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await onConfirm?.(candidates);
      onOpenChange?.(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="auto-approve-dialog">
        <DialogHeader>
          <div className="flex items-center gap-2 mb-1">
            <Sparkles className="w-5 h-5 text-violet-600" />
            <DialogTitle>Auto-approve high-confidence transactions</DialogTitle>
          </div>
          <DialogDescription>
            We found <b>{stats.count}</b> transaction{stats.count === 1 ? '' : 's'} where we&apos;re at least <b>{Math.round(threshold * 100)}%</b> sure of the right category. Approve them all in one click.
          </DialogDescription>
        </DialogHeader>

        {stats.count === 0 ? (
          <div className="text-center py-8" data-testid="auto-approve-empty">
            <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">No high-confidence transactions right now. Try lowering the threshold in Settings → AI Preferences.</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-2 my-2">
              <div className="p-2 rounded-md border border-border bg-muted/40">
                <p className="text-[10px] uppercase text-muted-foreground">Candidates</p>
                <p className="text-base font-semibold" data-testid="auto-approve-count">{stats.count}</p>
              </div>
              <div className="p-2 rounded-md border border-border bg-muted/40">
                <p className="text-[10px] uppercase text-muted-foreground">Avg confidence</p>
                <p className="text-base font-semibold">{Math.round(stats.avg * 100)}%</p>
              </div>
              <div className="p-2 rounded-md border border-border bg-muted/40">
                <p className="text-[10px] uppercase text-muted-foreground">Net</p>
                <p className="text-base font-semibold">{currency} {(stats.totalIn - stats.totalOut).toLocaleString()}</p>
              </div>
            </div>

            <div className="space-y-1.5 max-h-56 overflow-y-auto" data-testid="auto-approve-samples">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground px-1">Sample of what will be approved</p>
              {samples.map(t => {
                const isInflow = t.direction === 'inflow' || (t.amount || 0) > 0;
                const cat = pickCategoryDisplay(t) || {};
                const categoryLabel = cat.name || t.ai_category || '-';
                const categoryCode = cat.code || '';
                return (
                  <div key={t.id} className="flex items-center gap-3 p-2 rounded-md border border-border text-xs" data-testid={`auto-approve-sample-${t.id}`}>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{t.counterparty || t.description || 'Transaction'}</p>
                      <p className="text-[11px] text-muted-foreground truncate" data-testid={`auto-approve-sample-gl-${t.id}`}>
                        <span className="font-medium text-foreground/80">{categoryLabel}</span>
                        {categoryCode && <span className="font-mono opacity-60"> · {categoryCode}</span>}
                      </p>
                    </div>
                    <Badge variant="secondary" className="text-[10px]">{Math.round((t.ai_confidence || t.confidence || 0) * 100)}%</Badge>
                    <span className={`font-semibold tabular-nums whitespace-nowrap ${isInflow ? 'text-emerald-700' : 'text-rose-700'}`}>
                      {isInflow ? '+' : '-'}{Math.abs(t.amount || 0).toLocaleString()}
                    </span>
                  </div>
                );
              })}
              {candidates.length > samples.length && (
                <p className="text-[11px] text-muted-foreground px-1">… and {candidates.length - samples.length} more.</p>
              )}
            </div>

            <div className="flex items-start gap-2 p-2.5 rounded-md bg-amber-50 border border-amber-200 mt-1" data-testid="auto-approve-warning">
              <AlertTriangle className="w-3.5 h-3.5 text-amber-600 mt-0.5 flex-shrink-0" />
              <p className="text-[11px] text-amber-900">
                Anything we get wrong, you can still fix afterwards. Every approval is reversible from the In Books tab.
              </p>
            </div>
          </>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange?.(false)} disabled={busy} data-testid="auto-approve-cancel">
            Cancel
          </Button>
          {stats.count > 0 && (
            <Button
              onClick={handleConfirm}
              disabled={busy}
              className="bg-violet-600 hover:bg-violet-700 text-white"
              data-testid="auto-approve-confirm"
            >
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
              Approve all {stats.count}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AutoApproveDialog;
